const rowsOf = (data) => (data.reports || []).flatMap((report) => (report.rows || []).map((row) => ({ ...row, report })));
const quantity = (value) => Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const percentage = (value) => `${value.toFixed(2)}%`;
const rate = ({ good, scrap }) => good + scrap > 0 ? 100 * scrap / (good + scrap) : 0;

export const getMonthlyScrapModel = (data) => {
  const machines = new Map();
  const days = new Map();
  const shifts = new Map();
  const rows = rowsOf(data).filter((row) => Number(row.bags_good || 0) > 0 || Number(row.scrap || 0) > 0);
  const totals = { good: 0, scrap: 0, records: rows.length };
  rows.forEach((row) => {
    const good = Number(row.bags_good || 0);
    const scrap = Number(row.scrap || 0);
    totals.good += good;
    totals.scrap += scrap;
    [[machines, row.report.machine?.code || 'SIN MÁQUINA'], [days, row.report.date || 'SIN FECHA'], [shifts, row.report.shift?.code || 'SIN TURNO']].forEach(([groups, label]) => {
      if (!groups.has(label)) groups.set(label, { label, good: 0, scrap: 0 });
      const group = groups.get(label);
      group.good += good;
      group.scrap += scrap;
    });
  });
  const values = (groups) => Array.from(groups.values(), (group) => ({ ...group, rate: rate(group) }));
  return {
    ...totals,
    rate: rate(totals),
    machines: values(machines).sort((a, b) => a.scrap - b.scrap || a.label.localeCompare(b.label, undefined, { numeric: true })),
    days: values(days).sort((a, b) => a.label.localeCompare(b.label)),
    shifts: values(shifts).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
  };
};

const text = (doc, value, x, y, size = 10, color = [51, 65, 85], bold = false, options = {}) => {
  doc.setFont('helvetica', bold ? 'bold' : 'normal');
  doc.setFontSize(size);
  doc.setTextColor(...color);
  doc.text(String(value), x, y, options);
};

const frame = (doc, title, x, y, width, height) => {
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.7);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, width, height, 4, 4, 'FD');
  text(doc, title, x + width / 2, y + 23, 12, [51, 65, 85], true, { align: 'center' });
};

const ticks = (max) => {
  if (max <= 0) return { step: 0.5, max: 2.5 };
  const rough = max / 5;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].find((value) => value * magnitude >= rough) * magnitude;
  return { step, max: Math.ceil(max / step) * step };
};

const horizontalChart = (doc, entries, x, y, width, height) => {
  frame(doc, 'Scrap por máquina', x, y, width, height);
  const left = x + 46;
  const right = x + width - 54;
  const top = y + 46;
  const bottom = y + height - 45;
  const scale = ticks(Math.max(...entries.map((entry) => entry.scrap), 0));
  for (let value = 0; value <= scale.max + scale.step / 2; value += scale.step) {
    const pos = left + (right - left) * value / scale.max;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(pos, top, pos, bottom);
    text(doc, quantity(value), pos, bottom + 14, 7, [100, 116, 139], false, { align: 'center' });
  }
  const slot = (bottom - top) / Math.max(entries.length, 1);
  entries.forEach((entry, index) => {
    const center = top + slot * (index + 0.5);
    const barWidth = (right - left) * entry.scrap / scale.max;
    doc.setFillColor(71, 112, 177);
    doc.rect(left, center - Math.min(12, slot * 0.5) / 2, barWidth, Math.min(12, slot * 0.5), 'F');
    text(doc, entry.label, left - 8, center + 3, 8, [71, 85, 105], false, { align: 'right' });
    text(doc, quantity(entry.scrap), left + barWidth + 5, center + 3, 8);
  });
  text(doc, 'Bolsas de scrap', (left + right) / 2, bottom + 31, 9, [100, 116, 139], false, { align: 'center' });
};

const percentChart = (doc, entries, title, x, y, width, height, line = false) => {
  frame(doc, title, x, y, width, height);
  const left = x + 49;
  const right = x + width - 24;
  const top = y + 48;
  const bottom = y + height - (line ? 68 : 37);
  const scale = ticks(Math.max(...entries.map((entry) => entry.rate), 0) * 1.15);
  for (let value = 0; value <= scale.max + scale.step / 2; value += scale.step) {
    const pos = bottom - (bottom - top) * value / scale.max;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(left, pos, right, pos);
    text(doc, percentage(value), left - 7, pos + 3, 7, [100, 116, 139], false, { align: 'right' });
  }
  const dates = entries.map((entry) => Date.parse(`${entry.label}T00:00:00Z`));
  const useDates = line && dates.every(Number.isFinite) && dates.length > 1;
  const span = useDates ? dates[dates.length - 1] - dates[0] : 0;
  let previous;
  entries.forEach((entry, index) => {
    const slot = (right - left) / Math.max(entries.length, 1);
    const center = line && span > 0
      ? left + 8 + (right - left - 16) * (dates[index] - dates[0]) / span
      : left + slot * (index + 0.5);
    const pos = bottom - (bottom - top) * entry.rate / scale.max;
    if (line) {
      if (previous) {
        doc.setDrawColor(234, 132, 39);
        doc.setLineWidth(1.8);
        doc.line(previous[0], previous[1], center, pos);
      }
      doc.setFillColor(49, 91, 134);
      doc.circle(center, pos, 2.3, 'F');
      previous = [center, pos];
      const stride = Math.max(1, Math.ceil(entries.length / 12));
      if (index % stride === 0 || index === entries.length - 1) {
        const dateLabel = /^\d{4}-\d{2}-\d{2}$/.test(entry.label) ? `${entry.label.slice(8)}/${entry.label.slice(5, 7)}` : entry.label;
        text(doc, dateLabel, center, bottom + 20, 7, [100, 116, 139], false, { align: 'center' });
      }
    } else {
      doc.setFillColor(112, 151, 204);
      const barWidth = Math.min(60, slot * 0.45);
      doc.rect(center - barWidth / 2, pos, barWidth, bottom - pos, 'F');
      text(doc, percentage(entry.rate), center, pos - 7, 9, [71, 85, 105], false, { align: 'center' });
      text(doc, entry.label, center, bottom + 18, 9, [71, 85, 105], false, { align: 'center' });
    }
  });
  text(doc, '% Scrap', x + 12, top - 10, 8, [100, 116, 139]);
};

export const drawMonthlyScrapDashboard = (doc, data, margin = 36) => {
  const model = getMonthlyScrapModel(data);
  const width = doc.internal.pageSize.getWidth() - margin * 2;
  const pageHeight = doc.internal.pageSize.getHeight();
  const summaryY = 112;
  const cellWidth = width / 4;
  [
    ['Producción buena', quantity(model.good), [15, 23, 42]],
    ['Scrap total', quantity(model.scrap), [194, 65, 12]],
    ['Nivel de desperdicio', percentage(model.rate), [234, 132, 39]],
    ['Registros analizados', quantity(model.records), [15, 23, 42]],
  ].forEach(([label, value, color], index) => {
    const x = margin + index * cellWidth;
    doc.setFillColor(49, 76, 115);
    doc.rect(x, summaryY, cellWidth, 25, 'F');
    doc.setFillColor(237, 242, 248);
    doc.rect(x, summaryY + 25, cellWidth, 43, 'F');
    text(doc, label, x + cellWidth / 2, summaryY + 17, 10, [255, 255, 255], true, { align: 'center' });
    text(doc, value, x + cellWidth / 2, summaryY + 54, 19, color, true, { align: 'center' });
  });
  text(doc, '% Scrap = Scrap / (Producción buena + Scrap). Registros analizados: renglones con producción buena o Scrap.', margin, summaryY + 85, 9, [100, 116, 139]);
  if (!model.records) {
    text(doc, 'Sin datos para el periodo seleccionado.', margin + width / 2, 270, 14, [100, 116, 139], false, { align: 'center' });
    return;
  }
  const top = 218;
  const height = pageHeight - top - 46;
  const gap = 14;
  const leftWidth = width * 0.49;
  const rightX = margin + leftWidth + gap;
  const rightWidth = width - leftWidth - gap;
  // Mantener todas las máquinas legibles; continuar en páginas adicionales si hay más de 30.
  const chunks = Array.from({ length: Math.ceil(model.machines.length / 30) }, (_, index) => model.machines.slice(index * 30, (index + 1) * 30));
  horizontalChart(doc, chunks[0], margin, top, leftWidth, height);
  percentChart(doc, model.days, 'Tasa diaria de scrap', rightX, top, rightWidth, height * 0.54, true);
  percentChart(doc, model.shifts, 'Porcentaje de scrap por turno', rightX, top + height * 0.54 + gap, rightWidth, height * 0.46 - gap);
  chunks.slice(1).forEach((chunk) => {
    doc.addPage();
    horizontalChart(doc, chunk, margin, margin, width, pageHeight - margin * 2);
  });
};
